import { useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import { usePreviewLanguage, setPreviewLanguage } from '@/hooks/useLanguagePreview';
import {
  READER_REGIONS,
  NEWSLETTER_INTERESTS,
  LANGUAGES,
} from '@/lib/multilingual';

/**
 * Demonstrates that a reader's region, language and newsletter interests are
 * three independent preferences. Nothing is saved and the reader's language is
 * never inferred from their location.
 */
export default function ReaderPreferences() {
  const lang = usePreviewLanguage();
  const [region, setRegion] = useState('all');
  const [interests, setInterests] = useState<string[]>(['tournaments', 'course-news']);
  const [saved, setSaved] = useState(false);

  const toggleInterest = (id: string) => {
    setSaved(false);
    setInterests((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const regionLabel = READER_REGIONS.find((r) => r.id === region)?.label ?? 'All regions';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Reader preferences</h2>
        <DemoBadge label="Illustrative — nothing is saved" />
      </div>

      <p className="text-sm text-foreground-600 max-w-3xl">
        Region, language and newsletter interests are three <strong className="font-semibold text-foreground-800">independent</strong> choices. Picking a region never changes the reading language, and a reader&apos;s language is never guessed from where they are.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Region */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
            <p className="font-heading font-bold text-lg text-foreground-950">Preferred region</p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {READER_REGIONS.map((r) => {
              const on = region === r.id;
              return (
                <button key={r.id} type="button" onClick={() => { setRegion(r.id); setSaved(false); }} className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}>
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Language */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center"><i className="ri-translate-2"></i></span>
            <p className="font-heading font-bold text-lg text-foreground-950">Preferred language</p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {LANGUAGES.map((l) => {
              const on = lang === l.code;
              return (
                <button key={l.code} type="button" onClick={() => { setPreviewLanguage(l.code); setSaved(false); }} className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-accent-500 border-accent-500 text-foreground-950' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-accent-400'}`}>
                  {l.native}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-foreground-500">Chosen by the reader — never detected from location or device.</p>
        </div>

        {/* Interests */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center"><i className="ri-mail-star-line"></i></span>
            <p className="font-heading font-bold text-lg text-foreground-950">Newsletter interests</p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {NEWSLETTER_INTERESTS.map((i) => {
              const on = interests.includes(i.id);
              return (
                <button key={i.id} type="button" onClick={() => toggleInterest(i.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-secondary-500 border-secondary-500 text-foreground-950' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-secondary-400'}`}>
                  <span className="w-3.5 h-3.5 flex items-center justify-center">{on ? <i className="ri-checkbox-circle-line"></i> : <i className="ri-add-circle-line"></i>}</span>
                  {i.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary + save */}
      <div className="bg-background-100 border border-background-200 rounded-lg p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="text-foreground-600">Region: <strong className="font-semibold text-foreground-950">{regionLabel}</strong></span>
            <span className="text-foreground-600">Language: <strong className="font-semibold text-foreground-950">{LANGUAGES.find((l) => l.code === lang)?.native}</strong></span>
            <span className="text-foreground-600">Interests: <strong className="font-semibold text-foreground-950">{interests.length} selected</strong></span>
          </div>
          <button type="button" onClick={() => setSaved(true)} className="inline-flex items-center gap-2 bg-foreground-950 hover:bg-foreground-800 text-background-50 font-label font-bold uppercase tracking-wider text-[11px] px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-bookmark-line"></i></span>Save preferences
          </button>
        </div>
        {saved && (
          <p className="mt-3 bg-primary-50 border border-primary-200 text-primary-800 text-sm rounded-md px-4 py-2.5 flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-checkbox-circle-line"></i></span>
            Preferences captured in this demo only — no subscriber record was created or changed.
          </p>
        )}
      </div>
    </div>
  );
}